import type { RequiredGeneric } from '@pba/react-utils';
import type { GivingInterval } from 'models';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Form, Radio, Row, Switch, Text } from 'ui';
import { useItemSpanSize, useSubscriptionPlans } from '../../hooks';

export type RecurringFormValues = { recurring?: GivingInterval; showRecurring: boolean };

type P = {
  intervals: GivingInterval[];
};

const RecurringFormContent = <T extends RecurringFormValues = never>({ intervals }: RequiredGeneric<T, P>) => {
  const { t } = useTranslation();
  const itemSpanSize = useItemSpanSize();
  const { changeIntervalToText, getTranslate } = useSubscriptionPlans();
  const onlyRecurringIntervals = useMemo(() => intervals.filter((interval) => interval !== 'one_time'), [intervals]);

  return onlyRecurringIntervals.length > 0 ? (
    <>
      {/* eslint-disable-next-line no-restricted-syntax -- TODO: fix later */}
      <Form.Item shouldUpdate>
        {({ getFieldValue }) => {
          const isCustomAmount = getFieldValue('showCustomAmount') === true;

          return (
            <Row gutter="xs">
              <Col>
                <Form.Item<RecurringFormValues>
                  name="showRecurring"
                  noStyle
                  valuePropName={isCustomAmount ? 'notExistingPropName' : 'checked'}
                >
                  <Switch disabled={isCustomAmount} size="small" />
                </Form.Item>
              </Col>
              <Col>
                <Text size="small">Make my gift recurring</Text>
              </Col>
            </Row>
          );
        }}
      </Form.Item>
      {/* eslint-disable-next-line no-restricted-syntax -- TODO: fix later */}
      <Form.Item dependencies={['showRecurring', 'showCustomAmount']} noStyle>
        {({ getFieldValue }) =>
          getFieldValue('showRecurring') === true && getFieldValue('showCustomAmount') !== true ? (
            <Form.Item<RecurringFormValues> label="How often would you like to send this gift?" name="recurring">
              <Radio.Group fullWidth>
                <Row gutter={['xs', 'xs']}>
                  {onlyRecurringIntervals.map((item, index) => (
                    <Col key={item} span={itemSpanSize(index, onlyRecurringIntervals.length)}>
                      <Radio.Button
                        size="large"
                        tooltip={t('recurringPaymentInfo', {
                          interval: changeIntervalToText(item),
                        })}
                        value={item}
                      >
                        {getTranslate(item)}
                      </Radio.Button>
                    </Col>
                  ))}
                </Row>
              </Radio.Group>
            </Form.Item>
          ) : null
        }
      </Form.Item>
    </>
  ) : null;
};

export default RecurringFormContent;
